import React, { createContext, useState, useEffect, useCallback } from 'react';
import videoDiscoveryService from '../services/videoDiscoveryService';

export const VideoDiscoveryContext = createContext({
  categories: {},
  categoriesLoaded: false,
  selectedVideos: [],
  recentSearches: [],
  toggleVideoSelection: () => { },
  clearSelectedVideos: () => { },
  addSearchToHistory: () => { },
  clearSearchHistory: () => { },
  isVideoSelected: () => false,
  
  selectedCategory: null,
  selectedSubcategory: null,
  selectedFormat: null,
  selectedTags: [],
  selectedRegion: null,
  rankingMethod: 'views',
  timeWindow: 'week',
  comparisonMetrics: ['views', 'likes', 'comments', 'engagement', 'duration', 'description_length', 'tags_count', 'age_limit', 'upload_age_days'],
  setSelectedCategory: () => {},
  setSelectedSubcategory: () => {},
  setSelectedFormat: () => {},
  setSelectedTags: () => {},
  setSelectedRegion: () => {},
  setRankingMethod: () => {},
  setTimeWindow: () => {},
  setComparisonMetrics: () => {},

  // Новые свойства для кэширования
  cachedVideos: [],
  cachedFilters: null,
  cacheTimestamp: null,
  setCacheData: () => {},
  clearVideoCache: () => {},
  isCacheValid: () => false,

  loading: false,
  loadingMore: false,
  error: null,
  setLoading: () => {},
  setLoadingMore: () => {},
  setError: () => {},
});

export const VideoDiscoveryProvider = ({ children }) => {
  const [categories, setCategories] = useState({});
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [rankingMethod, setRankingMethod] = useState('views');
  const [timeWindow, setTimeWindow] = useState('week');
  const [comparisonMetrics, setComparisonMetrics] = useState([
    'views', 'likes', 'comments', 'engagement', 'duration', 'description_length', 'tags_count', 'age_limit', 'upload_age_days'
  ]);

  // Новые состояния для кэширования
  const [cachedVideos, setCachedVideos] = useState([]);
  const [cachedFilters, setCachedFilters] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);

  // Константы для кэширования
  const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 часа
  const CACHE_KEY = 'videoDiscoveryCache';
  const CACHE_FILTERS_KEY = 'videoDiscoveryFilters';
  const CACHE_TIME_KEY = 'videoDiscoveryCacheTime';

  // Функция для создания хеша фильтров
  const createFiltersHash = useCallback((filters) => {
    const filterObj = {
      category: filters.selectedCategory || null,
      subcategory: filters.selectedSubcategory || null,
      format: filters.selectedFormat || null,
      tags: Array.isArray(filters.selectedTags) ? [...filters.selectedTags].sort() : [],
      region: filters.selectedRegion || null,
      ranking: filters.rankingMethod || 'views',
      timeWindow: filters.timeWindow || 'week'
    };
    return JSON.stringify(filterObj);
  }, []);

  // Получение текущих фильтров
  const getCurrentFilters = useCallback(() => ({
    selectedCategory,
    selectedSubcategory,
    selectedFormat,
    selectedTags,
    selectedRegion,
    rankingMethod,
    timeWindow
  }), [selectedCategory, selectedSubcategory, selectedFormat, selectedTags, selectedRegion, rankingMethod, timeWindow]);

  // Проверка валидности кэша
  const isCacheValid = useCallback(() => {
    if (!cacheTimestamp || !cachedFilters) {
      return false;
    }

    // Проверяем время жизни
    const now = Date.now();
    if (now - cacheTimestamp > CACHE_TTL) {
      return false;
    }

    // Проверяем совпадение фильтров
    const currentFiltersHash = createFiltersHash(getCurrentFilters());
    const cachedFiltersHash = createFiltersHash(cachedFilters);
    
    return currentFiltersHash === cachedFiltersHash;
  }, [cacheTimestamp, cachedFilters, createFiltersHash, getCurrentFilters]);

  // Сохранение данных в кэш
  const setCacheData = useCallback((videos, hasMore = true, currentPage = 1) => {
    const now = Date.now();
    const currentFilters = getCurrentFilters();
    
    setCachedVideos(videos);
    setCachedFilters(currentFilters);
    setCacheTimestamp(now);

    // Сохраняем в localStorage
    try {
      const cacheData = {
        videos,
        hasMore,
        currentPage,
        timestamp: now,
        filters: currentFilters
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(CACHE_FILTERS_KEY, createFiltersHash(currentFilters));
      localStorage.setItem(CACHE_TIME_KEY, now.toString());
    } catch (error) {
      // Игнорируем ошибки localStorage
      console.warn('Failed to save video cache to localStorage:', error);
    }
  }, [getCurrentFilters, createFiltersHash]);

  // Очистка кэша
  const clearVideoCache = useCallback(() => {
    setCachedVideos([]);
    setCachedFilters(null);
    setCacheTimestamp(null);

    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_FILTERS_KEY);
      localStorage.removeItem(CACHE_TIME_KEY);
    } catch (error) {
      // Игнорируем ошибки localStorage
    }
  }, []);

  // Загрузка кэша из localStorage при инициализации
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTime = localStorage.getItem(CACHE_TIME_KEY);
      
      if (cachedData && cachedTime) {
        const parsedData = JSON.parse(cachedData);
        const timestamp = parseInt(cachedTime);
        const now = Date.now();
        
        // Проверяем, не устарел ли кэш
        if (now - timestamp <= CACHE_TTL && parsedData.videos && parsedData.filters) {
          setCachedVideos(parsedData.videos);
          setCachedFilters(parsedData.filters);
          setCacheTimestamp(timestamp);
        } else {
          // Кэш устарел, очищаем
          clearVideoCache();
        }
      }
    } catch (error) {
      // При ошибке просто очищаем кэш
      clearVideoCache();
    }
  }, [clearVideoCache]);

  // Очистка кэша при изменении фильтров
  useEffect(() => {
    if (cachedFilters) {
      const currentFiltersHash = createFiltersHash(getCurrentFilters());
      const cachedFiltersHash = createFiltersHash(cachedFilters);
      
      if (currentFiltersHash !== cachedFiltersHash) {
        clearVideoCache();
      }
    }
  }, [selectedCategory, selectedSubcategory, selectedFormat, selectedTags, selectedRegion, rankingMethod, timeWindow, cachedFilters, createFiltersHash, getCurrentFilters, clearVideoCache]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cachedCategories = localStorage.getItem('videoCategoriesCache');
        const cacheTimestamp = localStorage.getItem('videoCategoriesCacheTime');
        const currentTime = new Date().getTime();

        if (cachedCategories && cacheTimestamp &&
          (currentTime - parseInt(cacheTimestamp) < 24 * 60 * 60 * 1000)) {
          setCategories(JSON.parse(cachedCategories));
          setCategoriesLoaded(true);
          return;
        }

        const categoriesData = await videoDiscoveryService.getCategories();
        setCategories(categoriesData);
        setCategoriesLoaded(true);

        localStorage.setItem('videoCategoriesCache', JSON.stringify(categoriesData));
        localStorage.setItem('videoCategoriesCacheTime', currentTime.toString());
      } catch (error) {
        const cachedCategories = localStorage.getItem('videoCategoriesCache');
        if (cachedCategories) {
          setCategories(JSON.parse(cachedCategories));
          setCategoriesLoaded(true);
        } else {
          setCategoriesLoaded(true);
        }
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    try {
      setSelectedVideos([]);

      const savedSelectedVideos = localStorage.getItem('selectedVideos');
      if (savedSelectedVideos) {
        try {
          const parsedVideos = JSON.parse(savedSelectedVideos);
          if (Array.isArray(parsedVideos)) {
            setSelectedVideos(parsedVideos);
          } else {
            localStorage.removeItem('selectedVideos');
          }
        } catch (parseError) {
          localStorage.removeItem('selectedVideos');
        }
      }

      const savedRecentSearches = localStorage.getItem('videoRecentSearches');
      if (savedRecentSearches) {
        try {
          const parsedSearches = JSON.parse(savedRecentSearches);
          if (Array.isArray(parsedSearches)) {
            setRecentSearches(parsedSearches);
          } else {
            localStorage.removeItem('videoRecentSearches');
          }
        } catch (parseError) {
          localStorage.removeItem('videoRecentSearches');
        }
      }
    } catch (error) {
      localStorage.removeItem('selectedVideos');
      localStorage.removeItem('videoRecentSearches');
    }
  }, []);

  useEffect(() => {
    if (!categoriesLoaded) {
      return;
    }

    try {
      const savedCategory = localStorage.getItem('videoSelectedCategory');
      if (savedCategory && categories.content_categories && categories.content_categories[savedCategory]) {
        setSelectedCategory(savedCategory);

        const savedSubcategory = localStorage.getItem('videoSelectedSubcategory');
        if (savedSubcategory &&
          Array.isArray(categories.content_categories[savedCategory].subcategories) &&
          categories.content_categories[savedCategory].subcategories.includes(savedSubcategory)) {
          setSelectedSubcategory(savedSubcategory);
        }
      } else if (savedCategory) {
        localStorage.removeItem('videoSelectedCategory');
        localStorage.removeItem('videoSelectedSubcategory');
      }

      const savedFormat = localStorage.getItem('videoSelectedFormat');
      if (savedFormat && categories.video_formats && Object.keys(categories.video_formats).includes(savedFormat)) {
        setSelectedFormat(savedFormat);
      } else if (savedFormat) {
        localStorage.removeItem('videoSelectedFormat');
      }

      const savedRegion = localStorage.getItem('videoSelectedRegion');
      if (savedRegion && savedRegion !== 'null') {
        setSelectedRegion(savedRegion);
      }

      const savedRankingMethod = localStorage.getItem('videoRankingMethod');
      if (savedRankingMethod) {
        setRankingMethod(savedRankingMethod);
      }

      const savedTimeWindow = localStorage.getItem('videoTimeWindow');
      if (savedTimeWindow) {
        setTimeWindow(savedTimeWindow);
      }

      const savedTags = localStorage.getItem('videoSelectedTags');
      if (savedTags) {
        try {
          const parsedTags = JSON.parse(savedTags);
          if (Array.isArray(parsedTags)) {
            setSelectedTags(parsedTags);
          }
        } catch (e) {
          localStorage.removeItem('videoSelectedTags');
        }
      }

      const savedComparisonMetrics = localStorage.getItem('videoComparisonMetrics');
      if (savedComparisonMetrics) {
        try {
          const parsedMetrics = JSON.parse(savedComparisonMetrics);
          if (Array.isArray(parsedMetrics)) {
            setComparisonMetrics(parsedMetrics);
          }
        } catch (e) {
          localStorage.removeItem('videoComparisonMetrics');
        }
      }
    } catch (error) {
      // Error loading filters from localStorage
    }
  }, [categoriesLoaded, categories]);

  const toggleVideoSelection = useCallback((videoId) => {
    if (!videoId) {
      return;
    }

    setSelectedVideos(prev => {
      if (!Array.isArray(prev)) {
        prev = [];
      }

      const isSelected = prev.includes(videoId);
      let newSelection;

      if (isSelected) {
        newSelection = prev.filter(id => id !== videoId);
      } else {
        newSelection = [...prev, videoId];
      }

      try {
        localStorage.setItem('selectedVideos', JSON.stringify(newSelection));
      } catch (error) {
        // Error saving to localStorage
      }

      return newSelection;
    });
  }, []);

  const clearSelectedVideos = useCallback(() => {
    setSelectedVideos([]);
    try {
      localStorage.removeItem('selectedVideos');
    } catch (error) {
      // Error removing from localStorage
    }
  }, []);

  const addSearchToHistory = useCallback((query) => {
    if (!query || typeof query !== 'string') return;

    setRecentSearches(prev => {
      const filtered = prev.filter(item => item !== query);
      const newSearches = [query, ...filtered].slice(0, 10);

      try {
        localStorage.setItem('videoRecentSearches', JSON.stringify(newSearches));
      } catch (error) {
        // Error saving to localStorage
      }

      return newSearches;
    });
  }, []);

  const clearSearchHistory = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('videoRecentSearches');
    } catch (error) {
      // Error removing from localStorage
    }
  }, []);

  const isVideoSelected = useCallback((id) => {
    return Array.isArray(selectedVideos) && selectedVideos.includes(id);
  }, [selectedVideos]);

  const setSelectedCategoryWithStorage = useCallback((category) => {
    setSelectedCategory(category);
    if (category) {
      localStorage.setItem('videoSelectedCategory', category);
    } else {
      localStorage.removeItem('videoSelectedCategory');
    }
    setSelectedSubcategory(null);
    localStorage.removeItem('videoSelectedSubcategory');
  }, []);

  const setSelectedSubcategoryWithStorage = useCallback((subcategory) => {
    setSelectedSubcategory(subcategory);
    if (subcategory) {
      localStorage.setItem('videoSelectedSubcategory', subcategory);
    } else {
      localStorage.removeItem('videoSelectedSubcategory');
    }
  }, []);

  const setSelectedFormatWithStorage = useCallback((format) => {
    const normalizedFormat = format && format !== 'None' && format !== 'null' && format.trim() !== '' ? format : null;
    
    setSelectedFormat(normalizedFormat);
    
    if (normalizedFormat) {
      localStorage.setItem('videoSelectedFormat', normalizedFormat);
    } else {
      localStorage.removeItem('videoSelectedFormat');
    }
  }, []);

  const setSelectedRegionWithStorage = useCallback((region) => {
    setSelectedRegion(region);
    if (region) {
      localStorage.setItem('videoSelectedRegion', region);
    } else {
      localStorage.removeItem('videoSelectedRegion');
    }
  }, []);

  const setRankingMethodWithStorage = useCallback((method) => {
    setRankingMethod(method);
    if (method) {
      localStorage.setItem('videoRankingMethod', method);
    }
  }, []);

  const setTimeWindowWithStorage = useCallback((window) => {
    setTimeWindow(window);
    if (window) {
      localStorage.setItem('videoTimeWindow', window);
    }
  }, []);

  const setSelectedTagsWithStorage = useCallback((tags) => {
    setSelectedTags(tags);
    localStorage.setItem('videoSelectedTags', JSON.stringify(tags));
  }, []);

  const setComparisonMetricsWithStorage = useCallback((metrics) => {
    setComparisonMetrics(metrics);
    localStorage.setItem('videoComparisonMetrics', JSON.stringify(metrics));
  }, []);

  const value = {
    categories,
    categoriesLoaded,
    selectedVideos,
    recentSearches,
    toggleVideoSelection,
    clearSelectedVideos,
    addSearchToHistory,
    clearSearchHistory,
    isVideoSelected,
    
    selectedCategory,
    selectedSubcategory,
    selectedFormat,
    selectedTags,
    selectedRegion,
    rankingMethod,
    timeWindow,
    comparisonMetrics,
    setSelectedCategory: setSelectedCategoryWithStorage,
    setSelectedSubcategory: setSelectedSubcategoryWithStorage,
    setSelectedFormat: setSelectedFormatWithStorage,
    setSelectedTags: setSelectedTagsWithStorage,
    setSelectedRegion: setSelectedRegionWithStorage,
    setRankingMethod: setRankingMethodWithStorage,
    setTimeWindow: setTimeWindowWithStorage,
    setComparisonMetrics: setComparisonMetricsWithStorage,

    // Новые методы для работы с кэшем
    cachedVideos,
    cachedFilters,
    cacheTimestamp,
    setCacheData,
    clearVideoCache,
    isCacheValid,

    loading,
    loadingMore,
    error,
    setLoading,
    setLoadingMore,
    setError,
  };

  return (
    <VideoDiscoveryContext.Provider value={value}>
      {children}
    </VideoDiscoveryContext.Provider>
  );
};

export default VideoDiscoveryProvider;